/** Labels humanos 4I — no exponer enums crudos en UI operativa. */

import type { FeeBreakdownItem, ReconciliationFinding } from './loan-financial.types';

export const CHARGE_TYPE_LABELS: Record<string, string> = {
  ADMIN_FEE: 'Administración',
  INSURANCE: 'Seguro',
  TAX: 'Impuestos',
  THIRD_PARTY_FEE: 'Terceros',
  RECOVERY_FEE: 'Recuperos',
  EARLY_CANCELLATION_FEE: 'Comisión cancelación',
  COLLECTION_FEE: 'Cobranza',
  OTHER: 'Otros',
};

export function chargeTypeLabel(code: string, opts?: { synthetic?: boolean }): string {
  if (opts?.synthetic || code === 'ADMIN_FEE_LEGACY' || code === 'LEGACY_ADMIN') {
    return 'Cargos administrativos (legacy)';
  }
  return CHARGE_TYPE_LABELS[code] ?? code.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

export function interestRecognitionLabel(method?: string | null): string {
  switch (method) {
    case 'ON_COLLECTION':
      return 'Al cobro';
    case 'ACCRUAL':
      return 'Por devengamiento';
    default:
      return method ? method.replace(/_/g, ' ') : '—';
  }
}

export function payoffPolicyLabel(policy?: string | null): string {
  switch (policy) {
    case 'FULL_CONTRACTUAL':
      return 'Plan contractual completo';
    case 'ACCRUED_TO_DATE':
      return 'Interés calculado hasta la fecha';
    case 'ACCRUED_PLUS_FEE':
      return 'Interés a la fecha + comisión de cancelación';
    default:
      return policy ? policy.replace(/_/g, ' ') : '—';
  }
}

export function payoffQuoteStatusLabel(status?: string | null): string {
  switch (status) {
    case 'ACTIVE':
      return 'Activa';
    case 'CONFIRMED':
      return 'Confirmada';
    case 'EXPIRED':
      return 'Vencida';
    case 'INVALIDATED':
      return 'Invalidada';
    case 'REVERSED':
      return 'Revertida';
    default:
      return status ?? '—';
  }
}

export function installmentStatusLabel(status?: string | null): string {
  switch (status) {
    case 'PENDING':
      return 'Pendiente';
    case 'PARTIAL':
      return 'Pago parcial';
    case 'OVERDUE':
      return 'Vencida';
    case 'PAID':
      return 'Pagada';
    case 'CANCELLED_BY_PAYOFF':
      return 'Cancelada por cancelación anticipada';
    case 'WAIVED':
      return 'Bonificada';
    case 'DEFAULTED':
      return 'Mora';
    case 'CANCELLED':
      return 'Anulada';
    default:
      return status ?? '—';
  }
}

export function allocationOrderLabel(order?: string[] | null): string {
  if (!order?.length) return '—';
  const map: Record<string, string> = {
    LATE_FEE: 'Punitorios',
    PENALTY: 'Punitorios',
    INTEREST: 'Intereses',
    FEE: 'Cargos',
    FEES: 'Cargos',
    PRINCIPAL: 'Capital',
  };
  return order.map((x) => map[x] ?? x).join(' → ');
}

export function reconciliationStatusUi(status?: string | null): {
  label: string;
  tone: 'ok' | 'warn' | 'error' | 'neutral';
  icon: string;
} {
  switch (status) {
    case 'OK':
      return { label: 'Conciliado', tone: 'ok', icon: '✓' };
    case 'WARNING':
      return { label: 'Requiere revisión', tone: 'warn', icon: '⚠' };
    case 'ERROR':
    case 'CRITICAL':
      return { label: 'Inconsistencias detectadas', tone: 'error', icon: '✕' };
    default:
      return { label: status ?? 'Sin verificar', tone: 'neutral', icon: '·' };
  }
}

/** Título humano primario; código técnico secundario. */
export function findingTitle(finding: Pick<ReconciliationFinding, 'code' | 'message'>): string {
  const TITLES: Record<string, string> = {
    PRINCIPAL_GL_MISMATCH:
      'El capital pendiente del préstamo no coincide con el saldo contable.',
    PRINCIPAL_SUBLEDGER_VS_GL:
      'El capital pendiente no coincide con el saldo registrado en Préstamos por cobrar.',
    INTEREST_RECEIVABLE_MISMATCH:
      'Los intereses por cobrar no coinciden con el saldo contable.',
    PAYMENT_ALLOCATION_MISMATCH: 'La imputación del pago no cuadra con el monto aplicado.',
    PAYMENT_WITHOUT_JOURNAL: 'Hay un pago sin asiento contable asociado.',
    LEGACY_PENALTY_DIVERGENCE:
      'Este préstamo utiliza una política histórica de cálculo de punitorios.',
    LEGACY_SYNTHETIC_ADMIN_FEE: 'Cargos administrativos sintetizados (legacy).',
  };
  return TITLES[finding.code] ?? finding.message ?? finding.code;
}

export function findingExplanation(finding: Pick<ReconciliationFinding, 'code' | 'message'>): string {
  if (finding.code === 'LEGACY_PENALTY_DIVERGENCE') {
    return 'El préstamo conserva las reglas vigentes al momento de su originación. Esto no implica necesariamente una inconsistencia contable.';
  }
  if (finding.code === 'PRINCIPAL_GL_MISMATCH' || finding.code === 'PRINCIPAL_SUBLEDGER_VS_GL') {
    return 'El capital pendiente contractual debería coincidir con la exposición de capital en cuentas por cobrar (1.2.01 + 1.2.03).';
  }
  return finding.message;
}

export function findingCategoryGroup(category?: string | null): string {
  switch (category) {
    case 'ACCOUNTING':
    case 'SUBLEDGER':
      return 'Contabilidad';
    case 'PAYMENT':
    case 'ALLOCATION':
      return 'Pagos';
    case 'CHARGE':
      return 'Cargos';
    case 'PENALTY':
      return 'Punitorios';
    case 'REVERSAL':
      return 'Reversas';
    case 'POLICY':
      return 'Políticas';
    case 'LEGACY':
      return 'Legacy';
    case 'PAYOFF':
      return 'Cancelación anticipada';
    case 'PAYROLL':
      return 'Haberes';
    default:
      return category ?? 'Otros';
  }
}

export function groupFeeBreakdown(items: FeeBreakdownItem[] | undefined): Array<{
  chargeType: string;
  label: string;
  outstanding: number;
  amount: number;
  synthetic?: boolean;
}> {
  if (!items?.length) return [];
  const map = new Map<string, { chargeType: string; label: string; outstanding: number; amount: number; synthetic?: boolean }>();
  for (const it of items) {
    const key = it.synthetic ? `${it.chargeType}:legacy` : it.chargeType;
    const prev = map.get(key);
    const label = chargeTypeLabel(it.chargeType, { synthetic: it.synthetic });
    if (prev) {
      prev.outstanding += Number(it.outstanding ?? 0);
      prev.amount += Number(it.amount ?? 0);
    } else {
      map.set(key, {
        chargeType: it.chargeType,
        label,
        outstanding: Number(it.outstanding ?? 0),
        amount: Number(it.amount ?? 0),
        synthetic: it.synthetic,
      });
    }
  }
  return [...map.values()];
}

export function humanizePayoffApiError(error: unknown): { code?: string; message: string; stale?: boolean } {
  const ax = error as {
    response?: { status?: number; data?: { message?: string | string[]; code?: string; error?: string } };
    message?: string;
  };
  const data = ax.response?.data;
  const rawMsg = Array.isArray(data?.message)
    ? data.message.filter((m) => typeof m === 'string').join('. ')
    : typeof data?.message === 'string'
      ? data.message
      : typeof data?.error === 'string'
        ? data.error
        : ax.message;
  const code = data?.code;
  const text = `${code ?? ''} ${rawMsg ?? ''}`.toUpperCase();
  if (text.includes('PAYOFF_QUOTE_STALE') || text.includes('QUOTE_STALE') || text.includes('STALE')) {
    return {
      code: 'PAYOFF_QUOTE_STALE',
      stale: true,
      message:
        'El saldo del préstamo cambió desde que se generó la cotización. Generá una nueva cotización para continuar.',
    };
  }
  if (text.includes('EXPIRED') || text.includes('QUOTE_EXPIRED')) {
    return {
      code: 'PAYOFF_QUOTE_EXPIRED',
      message: 'La cotización venció. Generá una nueva cotización para continuar.',
    };
  }
  if (text.includes('UNSUPPORTED') || text.includes('POLICY')) {
    return {
      code: code ?? 'POLICY_UNSUPPORTED',
      message:
        'No se puede realizar esta operación con la configuración actual del préstamo. Revisá la política financiera.',
    };
  }
  if (ax.response?.status === 403) {
    return { code: 'FORBIDDEN', message: 'No tenés permiso para esta operación.' };
  }
  return {
    code,
    message: typeof rawMsg === 'string' && rawMsg && !rawMsg.includes('[object')
      ? rawMsg
      : 'No se pudo completar la operación. Reintentá.',
  };
}
